import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { ProfileContext } from "@/context/ProfileContext";
import { supabase } from "@/lib/supabaseClient";
import { loadStripe } from "@stripe/stripe-js";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Profile blocks
import ProfileInfo from "../components/Profile/ProfileInfo";
import EditProfile from "../components/Profile/EditProfile";
import ConsentementIA from "../components/Profile/ConsentementIA";
import ExpertBillingCard from "@/components/billing/ExpertBillingCard";
import PartnerBillingCard from "@/components/billing/PartnerBillingCard";
import AdvisorSubscriptionContainer from "@/components/advisor/AdvisorSubscriptionContainer";
import FiscalPreferencesManager from "@/components/Profile/FiscalPreferencesManager";
import BillingIdentitySection from "@/components/BillingIdentitySection";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PLAN_PRICE_ENV = {
  standard: "REACT_APP_STRIPE_PRICE_STANDARD",
  premium: "REACT_APP_STRIPE_PRICE_PREMIUM",
  art_advisor: "REACT_APP_STRIPE_PRICE_ART_ADVISOR",
  art_advisor_premium: "REACT_APP_STRIPE_PRICE_ART_ADVISOR_PREMIUM",
  family_office: "REACT_APP_STRIPE_PRICE_FAMILY_OFFICE",
};

function getPlanPriceId(plan) {
  return process.env[PLAN_PRICE_ENV[plan]] || "";
}

function normalize(value, fallback = "") {
  return String(value || fallback).toLowerCase();
}

function hasAiEntitlement(profile = {}) {
  const role = normalize(profile?.role, "user");
  const plan = normalize(profile?.plan || profile?.subscription_plan, "standard");

  if (profile?.is_demo === true) return true;
  if (["admin", "team"].includes(role)) return true;
  if (role === "user") return ["premium", "family_office"].includes(plan);
  if (role === "expert" || role === "art_advisor") {
    return ["art_advisor", "art_advisor_premium", "premium"].includes(plan);
  }
  if (role === "family_office") return true;
  return ["premium", "art_advisor", "art_advisor_premium", "family_office"].includes(plan);
}

function getLocale(language) {
  const code = normalize(language, "fr").split("-")[0];
  if (code === "de") return "de-DE";
  if (code === "en") return "en-GB";
  return "fr-FR";
}

function isActiveStripeStatus(status) {
  return ["active", "trialing"].includes(normalize(status));
}

function getPlanMeta(plan, t) {
  const labels = {
    standard: t("profile.plans.standard.label"),
    premium: t("profile.plans.premium.label"),
    art_advisor: t("profile.plans.artAdvisor.label"),
    art_advisor_premium: t("profile.plans.artAdvisorPremium.label"),
    family_office: t("profile.plans.familyOffice.label"),
  };

  const descriptions = {
    standard: t("profile.plans.standard.description"),
    premium: t("profile.plans.premium.description"),
    art_advisor: t("profile.plans.artAdvisor.description"),
    art_advisor_premium: t("profile.plans.artAdvisorPremium.description"),
    family_office: t("profile.plans.familyOffice.description"),
  };

  return {
    id: plan,
    label: labels[plan] || plan,
    description: descriptions[plan] || "",
    priceId: getPlanPriceId(plan),
  };
}

function FiscalPreferenceCard() {
  const { user } = useAuth();
  const { t } = useTranslation();

  const [value, setValue] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let ignore = false;

    async function loadFiscalPreference() {
      if (!user?.id) {
        setLoaded(true);
        return;
      }

      const { data, error } = await supabase
        .from("profiles")
        .select("fiscal_regime")
        .eq("id", user.id)
        .single();

      if (!ignore) {
        if (!error) setValue(data?.fiscal_regime ?? null);
        setLoaded(true);
      }
    }

    loadFiscalPreference();

    return () => {
      ignore = true;
    };
  }, [user?.id]);

  const save = useCallback(
    async (nextValue) => {
      if (!user?.id) return;

      setSaving(true);
      const { error } = await supabase
        .from("profiles")
        .update({ fiscal_regime: nextValue || null })
        .eq("id", user.id);
      setSaving(false);

      if (error) {
        alert(t("profile.fiscal.error.save"));
        return;
      }

      setValue(nextValue || null);
    },
    [t, user?.id]
  );

  return (
    <div className="p-6 md:p-7 bg-white rounded-2xl border border-neutral-200 shadow-sm">
      <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: "Georgia, serif" }}>
        {t("profile.fiscal.title")}
      </h2>
      <p className="text-sm text-gray-600 mb-3">
        {t("profile.fiscal.description")}
      </p>

      <div className="max-w-sm">
        <select
          className="w-full border border-gray-300 p-2 rounded"
          value={value || ""}
          onChange={(event) => save(event.target.value || null)}
          disabled={!loaded || saving}
        >
          <option value="">{t("profile.fiscal.select.none")}</option>
          <option value="FR_FORFAIT_6_5">
            {t("profile.fiscal.select.FR_FORFAIT_6_5")}
          </option>
          <option value="FR_PLUS_VALUE">
            {t("profile.fiscal.select.FR_PLUS_VALUE")}
          </option>
          <option value="UK_CGT_SIMPLE_20">
            {t("profile.fiscal.select.UK_CGT_SIMPLE_20")}
          </option>
          <option value="DE_PRIVATE_1Y_RULE">
            {t("profile.fiscal.select.DE_PRIVATE_1Y_RULE")}
          </option>
        </select>

        {saving && (
          <p className="text-xs text-gray-500 mt-2">
            {t("profile.fiscal.saving")}
          </p>
        )}
      </div>
    </div>
  );
}

function SubscriptionCard({ profile, role, isSatellite, isCollaborator, collabOwnerName }) {
  const { session } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = getLocale(i18n.resolvedLanguage || i18n.language);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [portalProcessing, setPortalProcessing] = useState(false);

  const currentPlan = normalize(profile?.plan, "standard");
  const stripeStatus = normalize(profile?.stripe_subscription_status, "inactive");
  const freeUntilDate = profile?.free_until ? new Date(profile.free_until) : null;
  const hasFreeAccess = Boolean(freeUntilDate && freeUntilDate > new Date());
  const partnerType = normalize(profile?.partner_type);
  const isInfluencer = ["influencer", "creator", "press"].includes(partnerType);

  const uiStatus = profile?.is_demo
    ? "demo"
    : hasFreeAccess || isInfluencer
    ? "free"
    : isActiveStripeStatus(stripeStatus)
    ? stripeStatus
    : "inactive";

  const billingDisabled = uiStatus === "free" || uiStatus === "demo" || isSatellite;

  const allowedPlans = useMemo(() => {
    if (role === "expert") return ["art_advisor", "art_advisor_premium"];
    if (role === "family_office") return ["family_office"];
    return ["standard", "premium"];
  }, [role]);

  const availablePlans = useMemo(
    () => allowedPlans.map((plan) => getPlanMeta(plan, t)),
    [allowedPlans, t]
  );

  const statusLabel =
    uiStatus === "demo"
      ? t("profile.subscription.status.demo")
      : uiStatus === "free"
      ? t("profile.subscription.status.freeAccess")
      : uiStatus === "active"
      ? t("profile.subscription.status.active")
      : uiStatus === "trialing"
      ? t("profile.subscription.status.trialing")
      : t("profile.subscription.status.inactive");

  const startCheckout = useCallback(
    async (plan) => {
      if (!session?.access_token || billingDisabled) return;

      const priceId = getPlanPriceId(plan);
      if (!priceId) {
        alert(
          t("profile.subscription.error.missingPrice")
        );
        return;
      }

      setProcessingPlan(plan);

      try {
        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error(
            t("profile.subscription.error.stripeUnavailable")
          );
        }

        // ⚠️ NE PAS appeler /api/create-checkout-session : /api est proxifié vers
        // le backend Python (→ 404). La fonction vit chez Supabase ; invoke()
        // tape la bonne URL avec le JWT automatiquement (même piège que PricingStripe.jsx).
        const { data: payload, error: fnError } = await supabase.functions.invoke(
          "create-checkout-session",
          { body: { priceId, plan } }
        );

        if (fnError || payload?.error) {
          throw new Error(payload?.error || fnError?.message || t("profile.subscription.error.checkoutUnavailable"));
        }

        if (payload?.url) {
          window.location.href = payload.url;
          return;
        }

        const { error } = await stripe.redirectToCheckout({ sessionId: payload.sessionId });
        if (error) throw error;
      } catch (error) {
        alert(
          t("profile.subscription.error.payment", { message: error.message })
        );
        setProcessingPlan(null);
      }
    },
    [billingDisabled, session?.access_token, t]
  );

  const openPortal = useCallback(async () => {
    if (!session?.access_token || billingDisabled) return;

    setPortalProcessing(true);

    try {
      const { data: payload, error: fnError } = await supabase.functions.invoke(
        "create-billing-portal-session",
        {}
      );

      if (fnError || payload?.error || !payload?.url) {
        throw new Error(payload?.error || fnError?.message || t("profile.subscription.error.portalUnavailable"));
      }

      window.location.href = payload.url;
    } catch (error) {
      alert(
        t("profile.subscription.error.portal", { message: error.message })
      );
      setPortalProcessing(false);
    }
  }, [billingDisabled, session?.access_token, t]);

  const currentPlanMeta = getPlanMeta(currentPlan, t);

  return (
    <section id="subscription" className="scroll-mt-24 bg-white rounded-2xl p-5 md:p-8 border border-neutral-200 shadow-sm mt-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-serif" style={{ fontFamily: "Georgia, serif" }}>
            {t("profile.subscription.sectionTitle")}
          </h2>
          <p className="text-sm text-neutral-600 mt-2">
            {t("profile.subscription.sectionSubtitle")}
          </p>
        </div>

        <span
          className={[
            "px-4 py-2 rounded-full text-sm font-semibold",
            uiStatus === "inactive"
              ? "bg-neutral-100 text-neutral-700"
              : uiStatus === "free"
              ? "bg-amber-100 text-amber-800"
              : uiStatus === "demo"
              ? "bg-indigo-100 text-indigo-800"
              : "bg-green-100 text-green-700",
          ].join(" ")}
        >
          {statusLabel}
        </span>
      </div>

      <div className="p-4 bg-neutral-50 rounded-xl mb-4">
        <div className="text-sm text-neutral-600 mb-1">
          {t("profile.subscription.currentPlanLabel")}
        </div>
        <div className="text-xl font-bold">{currentPlanMeta.label}</div>
        {currentPlanMeta.description && (
          <p className="text-sm text-neutral-600 mt-1">{currentPlanMeta.description}</p>
        )}
      </div>

      {(uiStatus === "free" || uiStatus === "demo" || isSatellite) && (
        <div className="p-4 bg-white border border-neutral-200 rounded-xl mb-4">
          {isSatellite ? (
            <>
              <div className="text-sm text-neutral-800 font-semibold">
                {t("profile.subscription.satellite.title")}
              </div>
              <div className="text-sm text-neutral-600 mt-1">
                {isCollaborator && collabOwnerName
                  ? t("profile.subscription.satellite.textNamed", { name: collabOwnerName })
                  : t("profile.subscription.satellite.text")
                }
              </div>
              <div className="text-xs text-neutral-400 mt-2">
                {t("profile.subscription.satellite.caption")}
              </div>
            </>
          ) : profile?.is_demo ? (
            <div className="text-sm text-neutral-700">
              {t("profile.subscription.demo.note")}
            </div>
          ) : (
            <>
              <div className="text-sm text-neutral-700">
                {t("profile.subscription.freeAccess.note")}
              </div>
              {hasFreeAccess && (
                <div className="text-xs text-neutral-500 mt-2">
                  {t("profile.subscription.freeAccess.until")} {" "}
                  <span className="font-semibold">{freeUntilDate?.toLocaleDateString?.(locale) || ""}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!isSatellite && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
        {availablePlans.map((plan) => {
          const isCurrent = plan.id === currentPlan;
          const isProcessing = processingPlan === plan.id;
          return (
            <div key={plan.id} className="border border-neutral-200 rounded-xl p-4 bg-white">
              <div className="font-semibold text-neutral-900">{plan.label}</div>
              <p className="text-sm text-neutral-600 mt-1 min-h-[42px]">{plan.description}</p>
              <button
                type="button"
                className={[
                  "mt-4 w-full px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200",
                  isCurrent || billingDisabled
                    ? "bg-neutral-200 text-neutral-500 cursor-not-allowed"
                    : "bg-black text-white hover:bg-neutral-800",
                ].join(" ")}
                disabled={isCurrent || billingDisabled || isProcessing}
                onClick={() => startCheckout(plan.id)}
              >
                {isCurrent
                  ? t("profile.subscription.currentPlanButton")
                  : isProcessing
                  ? t("profile.subscription.processing")
                  : t("profile.subscription.choosePlan")}
              </button>
            </div>
          );
        })}
      </div>

          <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => navigate("/upgrade-downgrade")}
          className="px-6 py-3 rounded-full font-semibold border-2 border-neutral-300 text-neutral-700 hover:border-black hover:text-black transition-all duration-300"
        >
          {t("profile.subscription.compareButton")}
        </button>

        <button
          type="button"
          onClick={openPortal}
          className={[
            "px-6 py-3 rounded-full font-semibold transition-all duration-300",
            billingDisabled
              ? "bg-neutral-300 text-neutral-600 cursor-not-allowed"
              : "bg-black text-white hover:bg-neutral-800",
          ].join(" ")}
          disabled={billingDisabled || portalProcessing}
          title={
            billingDisabled
              ? t("profile.subscription.freeAccess.noBilling")
              : ""
          }
        >
          {portalProcessing
            ? t("profile.subscription.portalProcessing")
            : t("profile.subscription.billingPortalButton")}
        </button>
      </div>
        </>
      )}
    </section>
  );
}

function IAConsentSection({ profile }) {
  const { t } = useTranslation();
  const profileCtx = useContext(ProfileContext);
  const [localUsesAi, setLocalUsesAi] = useState(Boolean(profile?.uses_ai));
  const aiEligible = hasAiEntitlement(profile);

  useEffect(() => {
    setLocalUsesAi(Boolean(profile?.uses_ai));
  }, [profile?.uses_ai]);

  const refreshProfile = useCallback(async () => {
    if (typeof profileCtx?.refreshProfile === "function") {
      await profileCtx.refreshProfile();
    }
    if (typeof profileCtx?.reloadProfile === "function") {
      await profileCtx.reloadProfile();
    }
  }, [profileCtx]);

  // ConsentementIA.jsx est l'unique writer de uses_ai / ai_signed_at.
  // Le parent reflète l'état et rafraîchit le contexte après une sauvegarde réussie.
  const handleConsentChange = useCallback(
    async (accepted, meta = {}) => {
      setLocalUsesAi(Boolean(accepted));
      if (meta?.source === "save") {
        await refreshProfile();
      }
    },
    [refreshProfile]
  );

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold">
            {t("profile.ia.title")}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {aiEligible
              ? t("profile.ia.description")
              : t("profile.ia.descriptionUnavailable")}
          </p>
        </div>
        <span
          className={[
            "px-3 py-1 rounded-full text-xs font-semibold",
            localUsesAi ? "bg-green-100 text-green-700" : "bg-neutral-100 text-neutral-700",
          ].join(" ")}
        >
          {!aiEligible
            ? t("profile.ia.status.unavailable")
            : localUsesAi
            ? t("profile.ia.status.active")
            : t("profile.ia.status.inactive")}
        </span>
      </div>

      <ConsentementIA embedded profile={profile} onConsentChange={handleConsentChange} />
    </section>
  );
}

// Palette ARTKIV (or / crème / encre)
const PROFILE_INK = "#1a1a1a";
const PROFILE_GOLD = "#a9843f";
const PROFILE_MUTED = "#8a8378";
const PROFILE_BORDER = "#e8e3d8";

/** En-tête de section homogène : sur-titre doré + titre Georgia + description + filet. */
function ProfileSection({ eyebrow, title, description, children, first }) {
  return (
    <section style={{ marginTop: first ? 0 : 44 }}>
      <div style={{ marginBottom: 18 }}>
        {eyebrow && (
          <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: PROFILE_GOLD, fontWeight: 700 }}>
            {eyebrow}
          </div>
        )}
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, lineHeight: 1.2, margin: "6px 0 0", color: PROFILE_INK, fontWeight: 600 }}>
          {title}
        </h2>
        {description && (
          <p style={{ fontSize: 13.5, color: PROFILE_MUTED, margin: "8px 0 0", maxWidth: 640, lineHeight: 1.5 }}>
            {description}
          </p>
        )}
        <div style={{ height: 1, background: `linear-gradient(90deg, ${PROFILE_BORDER}, transparent)`, marginTop: 16 }} />
      </div>
      {children}
    </section>
  );
}

function ProfilePage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Défilement vers la section ciblée par le hash (#subscription depuis le dashboard)
  React.useEffect(() => {
    const hash = window.location.hash?.replace("#", "");
    if (!hash) return;
    const timer = setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  const profileCtx = useContext(ProfileContext);
  const profile = profileCtx?.profile ?? null;
  const profileLoading = profileCtx?.profileLoading ?? false;

  const role = normalize(profile?.role, "user");
  const plan = normalize(profile?.plan, "standard");
  const partnerType = normalize(profile?.partner_type);

  const isTeam = role === "team";
  const isAdminRole = role === "admin";
  const isInternalArtkiv = isAdminRole || isTeam;
  const isFamilyOffice = role === "family_office" || plan === "family_office";
  const isArtAdvisor = role === "expert";
  const isExpertValuation = role === "partner" && partnerType === "expert_valuation";
  const isPartnerTransport = role === "partner" && partnerType === "transport";
  const isPartnerInsurance = role === "partner" && partnerType === "insurance";
  const isNotaire = role === "notaire";
  const isPartner = isExpertValuation || isPartnerTransport || isPartnerInsurance || isNotaire;
  const isExternalPartner = isPartner;
  // Détection collaborateur actif via table collaborators
  const [isCollaborator, setIsCollaborator] = React.useState(false);
  const [collabOwnerName, setCollabOwnerName] = React.useState("");

  React.useEffect(() => {
    if (!user?.id) return;
    supabase.from("collaborators")
      .select("id, created_by, status")
      .eq("user_id", user.id)
      .eq("status", "active")
      .limit(1).maybeSingle()
      .then(async ({ data: collab }) => {
        if (!collab) return;
        setIsCollaborator(true);
        if (collab.created_by) {
          const { data: owner } = await supabase.from("profiles")
            .select("first_name, last_name, company")
            .eq("id", collab.created_by).maybeSingle();
          if (owner) {
            const name = owner.company ||
              `${owner.first_name || ""} ${owner.last_name || ""}`.trim();
            setCollabOwnerName(name || "");
          }
        }
      });
  }, [user?.id]);

  const isSatellite = Boolean(
    profile?.collaborator_of ||
      profile?.owner_id ||
      profile?.parent_user_id ||
      profile?.main_collector_id ||
      isCollaborator  // détecté via table collaborators
  );

  const showConsumerCards = Boolean(profile) && !(isInternalArtkiv || isExternalPartner);

  const privacyConfig = useMemo(
    () => ({
      // Année de naissance = ciblage générationnel du COLLECTIONNEUR INDIVIDUEL
      // uniquement (pas d'année pour partenaires, art advisor, family office ni
      // fondation, qui sont des structures/pros).
      showBirthYearField:
        showConsumerCards && !isArtAdvisor && !isFamilyOffice && !profile?.foundation_mode,
      showFiscalPreferences: !isInternalArtkiv && !isExpertValuation && !isExternalPartner,
      showSubscriptionSection: showConsumerCards,
      showConsentIA: showConsumerCards,
    }),
    [isExpertValuation, isExternalPartner, isInternalArtkiv, showConsumerCards, isArtAdvisor, isFamilyOffice, profile?.foundation_mode]
  );

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <p className="text-sm text-gray-900 font-semibold">
            {t("auth.required.title")}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            {t("auth.required.text")}
          </p>
          <button className="mt-4 btn-artsy btn-primary-artsy" onClick={() => navigate("/login")}>
            {t("auth.login")}
          </button>
        </div>
      </div>
    );
  }

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gray-900 mx-auto mb-3" />
          <p className="text-gray-600 text-sm">
            {t("profile.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center max-w-lg">
          <p className="text-sm text-gray-900 font-semibold">
            {t("profile.error.missing.title")}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            {t("profile.error.missing.text")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 24px" }}>

      {/* Bannière démo */}
      {profile?.is_demo && (
        <div className="mb-8">
          <div className="relative rounded-xl border border-indigo-200 bg-indigo-50 p-3">
            <div className="absolute -top-2 -right-2 rotate-3">
              <span className="inline-block rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold tracking-wider text-white shadow">
                {t("profile.demo.ribbon.label")}
              </span>
            </div>
            <div className="flex items-center gap-2 text-indigo-700">
              <span className="inline-block h-2 w-2 rounded-full bg-indigo-500" />
              <p className="m-0 text-sm">
                {t("profile.demo.ribbon.text")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── En-tête de page ─────────────────────────────────────────────────── */}
      <header style={{ marginBottom: 40 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: PROFILE_GOLD, fontWeight: 700 }}>
          {t("profile.page.eyebrow", { defaultValue: "Espace personnel" })}
        </div>
        <h1 style={{ fontSize: "clamp(24px, 4vw, 34px)", fontFamily: "Georgia, serif", fontWeight: 600, margin: "8px 0 0", color: PROFILE_INK, lineHeight: 1.15 }}>
          {t("profile.page.title")}
        </h1>
        <p style={{ fontSize: 14.5, color: PROFILE_MUTED, margin: "10px 0 0", maxWidth: 680, lineHeight: 1.55 }}>
          {t("profile.page.subtitle", { defaultValue: "Gérez vos informations, votre abonnement et vos préférences de confidentialité." })}
        </p>
      </header>

      {/* ── Mon identité ────────────────────────────────────────────────────── */}
      <ProfileSection
        first
        eyebrow={t("profile.sections.identity.eyebrow", { defaultValue: "Profil" })}
        title={t("profile.sections.identity.title", { defaultValue: "Mon identité" })}
        description={t("profile.sections.identity.desc", { defaultValue: "Vos informations personnelles et vos coordonnées." })}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 24, alignItems: "start" }}
             className="profile-top-grid">
          <ProfileInfo showBirthYearField={privacyConfig.showBirthYearField} />
          <EditProfile showBirthYearField={privacyConfig.showBirthYearField} />
        </div>
      </ProfileSection>

      {/* ── Préférences & facturation ───────────────────────────────────────── */}
      {(privacyConfig.showFiscalPreferences || privacyConfig.showSubscriptionSection || isArtAdvisor || isFamilyOffice) && (
        <ProfileSection
          eyebrow={t("profile.sections.billing.eyebrow", { defaultValue: "Réglages" })}
          title={t("profile.sections.billing.title", { defaultValue: "Préférences & facturation" })}
          description={t("profile.sections.billing.desc", { defaultValue: "Votre abonnement, vos préférences fiscales et vos informations de facturation." })}
        >
          {(privacyConfig.showFiscalPreferences || privacyConfig.showSubscriptionSection || isArtAdvisor) && (
            <div style={{ display: "grid", gridTemplateColumns: privacyConfig.showFiscalPreferences ? "1fr 2fr" : "1fr", gap: 24, alignItems: "start" }}
                 className="profile-mid-grid">

              {privacyConfig.showFiscalPreferences && (
                <div>
                  {(isArtAdvisor || isFamilyOffice) ? (
                    <FiscalPreferencesManager />
                  ) : (
                    <FiscalPreferenceCard />
                  )}
                </div>
              )}

              <div>
                {privacyConfig.showSubscriptionSection && (
                  <SubscriptionCard
                    profile={profile}
                    role={isFamilyOffice ? "family_office" : role}
                    isSatellite={isSatellite}
                    isCollaborator={isCollaborator}
                    collabOwnerName={collabOwnerName}
                  />
                )}
                {isArtAdvisor && (
                  <div style={{ marginTop: 24 }}>
                    <AdvisorSubscriptionContainer />
                  </div>
                )}
                {isExpertValuation && <ExpertBillingCard />}
                {(isPartnerTransport || isPartnerInsurance) && (
                  <PartnerBillingCard partnerType={profile?.partner_type} />
                )}
              </div>
            </div>
          )}

          {/* Identité de facturation (B2B / facturation électronique) — PROS only.
              Le collectionneur individuel (B2C) n'est pas concerné. */}
          {(isArtAdvisor || isFamilyOffice) && (
            <div id="facturation" style={{ marginTop: 24, scrollMarginTop: 90 }}>
              <BillingIdentitySection profile={profile} />
            </div>
          )}
        </ProfileSection>
      )}

      {/* Demandes + Gestion des héritiers ont été déplacés dans le Dashboard
          (section « Contacts & accès ») — ce sont des interlocuteurs/accès de
          transmission plutôt que des réglages de profil. */}

      {/* ── Confidentialité & IA ────────────────────────────────────────────── */}
      {privacyConfig.showConsentIA && (
        <ProfileSection
          eyebrow={t("profile.sections.privacy.eyebrow", { defaultValue: "Données" })}
          title={t("profile.sections.privacy.title", { defaultValue: "Confidentialité & IA" })}
          description={t("profile.sections.privacy.desc", { defaultValue: "Vos consentements pour les fonctionnalités d'intelligence artificielle." })}
        >
          <IAConsentSection profile={profile} />
        </ProfileSection>
      )}
    </div>
  );
}

export default ProfilePage;